/* eslint-disable react/require-default-props */

import React, { useState, useEffect, useContext, Suspense } from 'react';
// eslint-disable-next-line object-curly-newline
import PropTypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { HdhomeSignup } from '@thd-olt-component-react/email-signup';
import { MediaGalleryDesktop } from '@thd-olt-component-react/media-gallery';
import { CustomProductPod } from './subcomponents/CustomProductPod';
import './quiz.scss';
import defaultQuiz from './data/quiz';
import { breadcrumbData } from './data/breadcrumbs';
import { Start } from './subcomponents/Start';
import { publish, getStartedAnalytics } from './analytics';
import { ProductsDataModel } from './dataModel';

// eslint-disable-next-line max-len
const Question = React.lazy(() => import(/* webpackChunkName: "question" */ './subcomponents/Question')
  .then((module) => ({ default: module.Question })));
const Results = React.lazy(() => import(/* webpackChunkName: "results" */ './subcomponents/Results')
  .then((module) => ({ default: module.Results })));

const MAX_QUESTIONS = 9;

const Quiz = ({
  quiz = null,
  styleId = null,
  location,
  navigate
}) => {
  const quizData = quiz || defaultQuiz;
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const _styleId = styleId || location?.state?.styleId;
  
  const [landingPageId, setLandingPageId] = useState(-1);
  const [currentQuestion, setCurrentQuestion] = useState(_styleId ? quizData.questions.length : -1);
  const [selections, setSelections] = useState({});
  
  useEffect(() => {
    if (window.getURLParameters) {
      const params = window.getURLParameters() || {};
      const { qid = -1 } = params;
      const pageId = Number(qid);
      const validPageId = (pageId >= 1 && pageId <= MAX_QUESTIONS) ? pageId - 1 : -1;
      setLandingPageId(validPageId);
      if (!_styleId && validPageId !== -1) {
        setCurrentQuestion(validPageId);
      }
    }
  }, []);
  
  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('quiz.ready');
    if (!_styleId) {
      publish('result.load', getStartedAnalytics());
    }
  }, []);

  const setStyleId = (style, omsIds) => {
    const state = {
      omsIds,
      styleId: style
    };
    const { pathname = '', search = '' } = location;
    const path = `${pathname}${search}`;
    navigate(path, { state });
  };

  const reset = () => {
    setStyleId(null, []);
    setCurrentQuestion(-1);
    setSelections({});
    publish('result.load', getStartedAnalytics());
  };

  const questionHandler = (nextQuestion) => {
    setCurrentQuestion(nextQuestion);
  };

  const {
    title, description, image, altText = '', questions = []
  } = quizData;
  return (
    <div className="quiz" data-component="Quiz">
      <div className="sui-max-w-screen-2xl sui-m-auto sui-p-2">
        <div>
          <Breadcrumbs data={breadcrumbData} className="quiz-breadcrumbs" />
        </div>
      </div>
      {currentQuestion === -1 && (
        <Start
          title={title}
          description={description}
          image={image}
          altText={altText}
          isMobile={isMobile}
          currentQuestion={currentQuestion}
          questionHandler={questionHandler}
        />
      )}
      {currentQuestion >= 0 && (
        <Suspense fallback={<div>Loading...</div>}>
          {questions.filter((_ques, qIndex) => qIndex === currentQuestion)
            .map((ques) => {
              return (
                <Question
                  key={currentQuestion}
                  ques={ques}
                  qIndex={currentQuestion}
                  questionsLength={questions.length}
                  selections={selections}
                  setSelections={(newSelection) => setSelections(newSelection)}
                  channel={channel}
                  questionHandler={questionHandler}
                />
              );
            })}
        </Suspense>
      )}
      {(currentQuestion === questions.length) && (
        <Suspense fallback={<div>Loading...</div>}>
          <Results
            location={location}
            quiz={quizData}
            reset={reset}
            selections={selections}
            setStyleId={setStyleId}
            styleId={_styleId}
          />
        </Suspense>
      )}
    </div>
  );
};

Quiz.dataModel = extend({}, ProductsDataModel, CustomProductPod, MediaGalleryDesktop, HdhomeSignup, Breadcrumbs);

Quiz.propTypes = {
  navigate: PropTypes.func.isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
    pathname: PropTypes.string,
    state: PropTypes.shape({
      styleId: PropTypes.string,
      omsIds: PropTypes.arrayOf(PropTypes.string)
    })
  }).isRequired,
  quiz: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.string,
    altText: PropTypes.string,
    questions: PropTypes.arrayOf(PropTypes.shape({
      question: PropTypes.string,
      layout: PropTypes.string,
      maxAllowed: PropTypes.number,
      sequence: PropTypes.number,
      guideline: PropTypes.string,
      allowNone: PropTypes.bool,
      choices: PropTypes.arrayOf(PropTypes.shape({
        url: PropTypes.string,
        styleCorrelation: PropTypes.string,
        absPosition: PropTypes.string,
        altText: PropTypes.string
      }))
    }))
  }),
  styleId: PropTypes.string
};

export { Quiz };
